// Uploads changed workouts and sets, and keeps the phone's "workout pack" fresh. Safe to call any time: the
// server keeps the newest edit of each record, so resending is harmless.
import {
  addDays,
  BodyweightList,
  CalendarResponse,
  ExerciseList,
  LogPack,
  ProgressResponse,
  SplitList,
  SyncResponse,
  todayIn,
  WorkoutList,
  type SetRecord,
  type User,
  type WorkoutDetail,
  type WorkoutRecord,
} from '@squats/shared';
import { useSyncExternalStore } from 'react';
import { api, errorText, isOffline } from '../api.ts';
import { cachedApi } from '../cache.ts';
import { db, type LocalSet, type LocalWorkout } from './db.ts';

// --- Status, for the "waiting to upload" line ---

/** `unreachable`: the last try couldn't reach the server (no signal, or gym Wi-Fi with no internet). */
type Status = { syncing: boolean; error: string | null; unreachable: boolean };
let status: Status = { syncing: false, error: null, unreachable: false };
const listeners = new Set<() => void>();
const setStatus = (s: Partial<Status>) => {
  status = { ...status, ...s };
  for (const l of listeners) l();
};
export function useSyncStatus() {
  return useSyncExternalStore(
    (l) => {
      listeners.add(l);
      return () => listeners.delete(l);
    },
    () => status,
  );
}

// --- Upload ---

// Records keep their owner: the planner's phone also holds the workouts it logs for others.
const strip = <T extends { dirty: 0 | 1; rejected?: string }>({ dirty: _d, rejected: _r, ...rest }: T) => rest;

let running: Promise<void> | null = null;
let again = false;
let timer: ReturnType<typeof setTimeout> | undefined;

/** Uploads soon (changes made in quick succession go up together). */
export function requestSync(delay = 400) {
  clearTimeout(timer);
  timer = setTimeout(() => void syncNow(), delay);
}

/** Uploads everything changed on this device (its records are the signed-in person's, or ones they log for others). */
export async function syncNow(): Promise<void> {
  if (running) {
    again = true;
    return running;
  }
  running = (async () => {
    setStatus({ syncing: true });
    try {
      do {
        again = false;
        await uploadAll();
      } while (again);
      setStatus({ error: null, unreachable: false });
    } catch (e) {
      // No signal: it waits in the queue. Anything else is worth showing.
      setStatus(isOffline(e) ? { error: null, unreachable: true } : { error: errorText(e), unreachable: false });
    } finally {
      setStatus({ syncing: false });
      running = null;
    }
  })();
  return running;
}

async function uploadAll() {
  for (let round = 0; round < 20; round++) {
    const waiting = (r: { rejected?: string }) => !r.rejected;
    const workouts = await db.workouts.where('dirty').equals(1).filter(waiting).limit(200).toArray();
    const sets = await db.sets.where('dirty').equals(1).filter(waiting).limit(1000).toArray();
    if (!workouts.length && !sets.length) return;
    const res = await api('/api/workouts/sync', SyncResponse, { body: { workouts: workouts.map(strip), sets: sets.map(strip) } });
    const rejected = new Map(res.rejected.map((r) => [r.id, r.reason]));
    await db.transaction('rw', db.workouts, db.sets, async () => {
      // Only mark clean what didn't change again while the upload was in flight.
      for (const w of workouts) {
        const cur = await db.workouts.get(w.id);
        if (cur?.updatedAt === w.updatedAt) await db.workouts.update(w.id, { dirty: 0, rejected: rejected.get(w.id) });
      }
      for (const s of sets) {
        const cur = await db.sets.get(s.id);
        if (cur?.updatedAt === s.updatedAt) await db.sets.update(s.id, { dirty: 0, rejected: rejected.get(s.id) });
      }
    });
  }
}

// --- Download ---

/** Adds the server's copy of a workout to the device, unless the device has newer or unsent changes. */
export async function mergeFromServer(d: WorkoutDetail) {
  const userId = d.userId;
  await db.transaction('rw', db.workouts, db.sets, async () => {
    const take = <T extends WorkoutRecord | SetRecord>(local: (T & { dirty: 0 | 1 }) | undefined, server: T) => !local || (!local.dirty && local.updatedAt <= server.updatedAt);
    if (take(await db.workouts.get(d.workout.id), d.workout)) await db.workouts.put({ ...d.workout, userId, dirty: 0 } satisfies LocalWorkout);
    for (const s of d.sets) if (take(await db.sets.get(s.id), s)) await db.sets.put({ ...s, userId, dirty: 0 } satisfies LocalSet);
  });
}

export const calendarPath = (from: string, to: string) => `/api/calendar?from=${from}&to=${to}`;
export const PACK_PATH = '/api/workouts/pack';
/** Someone's pack: yours, or the planner's copy of a training partner's. */
export const packPath = (userId: string, me: Pick<User, 'id'>) => (userId === me.id ? PACK_PATH : `${PACK_PATH}?user=${userId}`);
export const recentPath = (userId: string) => `/api/workouts?user=${userId}&limit=10`;

/**
 * Saves what logging needs with no signal: the week's plan, the exercise list, your splits, recent numbers,
 * and any workout you started on another device. Runs whenever the app opens online.
 */
export async function refreshPack(user: User) {
  const today = todayIn(user.timezone);
  const [pack, , , cal] = await Promise.all([
    cachedApi(user.id, PACK_PATH, LogPack),
    cachedApi(user.id, '/api/exercises', ExerciseList),
    cachedApi(user.id, '/api/splits', SplitList),
    cachedApi(user.id, calendarPath(today, addDays(today, 6)), CalendarResponse),
    cachedApi(user.id, recentPath(user.id), WorkoutList),
    cachedApi(user.id, '/api/bodyweight', BodyweightList),
    cachedApi(user.id, `/api/progress?user=${user.id}&weeks=12`, ProgressResponse),
  ]);
  if (!pack.saved) for (const d of pack.data.open) await mergeFromServer(d);
  // The planner logs workouts for everyone they train with, so keeps their numbers too.
  if (user.role !== 'admin') return;
  for (const other of cal.data.users.filter((u) => u.id !== user.id)) {
    const theirs = await cachedApi(user.id, packPath(other.id, user), LogPack);
    if (!theirs.saved) for (const d of theirs.data.open) await mergeFromServer(d);
  }
}
